import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { Formik, useFormik } from 'formik';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { getEmbroideryConfigKeyAndAsset } from '~/pdp/utils/getEmbroideryConfigKeyAndAsset';
import StyledSelect from '~/shared/components/StyledSelect';
import { PersonalizationTypes, StateFields } from '~/shared/utils/embroidery';
import { useAssets } from '~/techstyle-shared/react-assets';
import {
  Button,
  mobile,
  desktop,
  TextField,
  useProductContext,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';

import getImageUrl, {
  hasEmbroideryImg,
} from '../../../shared/utils/getImageUrl';
import EmbroideryPreviewImage from '../EmbroideryPreviewImage';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import SlidingSelector from '../SlidingSelector';

const CHAR_LIMIT = 20;

const TitleContainer = styled.div`
  display: flex;
  margin-bottom: 16px;
  ${desktop`
    justify-content: space-between;
  `}
  ${mobile`
    flex-direction: column;
  `}
`;

const Title = styled.div`
  ${({ theme: { typography } }) => typography.values.heading5small};
`;

const PreviewPanel = styled.div`
  position: relative;
  ${desktop`
    width: 50%;
  `}
`;

const CustomizationPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 28px 24px;
  ${desktop`
    width: 50%;
    padding: 56px;
  `}
`;

const StyledEmbroideryTextForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
`;

const TextFields = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 32px;
`;

const TextFieldWrapper = styled.div`
  position: relative;
  ${mobile`
    fieldset {
      border: 1px solid ${({ theme }) => theme.colors.borderLight};
      &[data-has-focus] {
        border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
        border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
      }
    }
    &:first-of-type {
      fieldset {
        border-top: 0px;
        border-radius: unset;
      }
    }
    &:last-of-type {
      fieldset {
        border-radius: 0 0 6px 6px;
        border-top: 0px;
      }
    }
  `}
`;

const CharacterCount = styled.span`
  ${({ theme: { typography } }) =>
    typography.variants.characterCountLabel.textStyle};
  color: ${({ theme: { colors } }) => colors.success};
  position: absolute;
  top: 4px;
  right: 16px;
`;

const EmbroideryTextSelectionWrapper = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  ${desktop`
    flex-direction: row;
  `}
`;

const SelectWrapper = styled.span`
  width: 100%;
  ${StyledSelect.Fieldset} {
    width: 100%;

    .has_error {
      ${css`
        border-color: ${({ theme }) => theme.colors.error};
      `}
    }

    .has_error ~ label {
      ${css`
        color: ${({ theme }) => theme.colors.error};
      `}
    }

    select {
      border-color: ${({ theme }) => theme.colors.borderLight};
      &:focus {
        border-color: ${({ theme }) => theme.colors.focusColor};
      }
    }

    > span {
      display: none;
    }
  }

  ${StyledSelect.Label} {
    font-size: 16px;
    padding-left: 8px;
    top: 8px;
    transition: ease-in 1ms top;

    ${({ $selectedValue }) =>
      $selectedValue &&
      `
        top: 0;
        font-size: 12px;
        padding: 3px 0 0 16px;
      `};
  }
  ${mobile`
    select {
      border: 1px solid ${({ theme }) => theme.colors.borderLight};
    }
    &:first-of-type {
      select {
        border-radius: 6px 6px 0 0;
        &:focus {
          border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
          border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
          border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
          border-bottom: 2px solid ${({ theme }) => theme.colors.focusColor};
        }
      }
    }
    &:last-of-type {
      select {
        border-radius: 0;
        border-top: 0px;
        &:focus {
          border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
          border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
          border-bottom: 2px solid ${({ theme }) => theme.colors.focusColor};
        }
      }
    }
  `}
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${desktop`
    justify-content: space-between;
    flex-direction: row;
    gap: 16px;
    margin-bottom: 16px;
  `}
`;

const StyledTextField = styled(TextField)`
  ${desktop`
    margin-bottom: 16px;
  `}
  && {
    fieldset {
      height: 48px;
    }
  }
`;

const ContinueButton = styled(Button)`
  width: 100%;
  margin: 0;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
  border-radius: 4px;
`;

const SelectorContainer = styled.div`
  position: absolute;
  bottom: 8px;
  padding: 0 8px;
  width: 100%;
`;

const StyledSelector = styled(SlidingSelector)`
  flex-direction: row-reverse;
  ${desktop`
    height: 44px;
  `}
`;

const Fee = styled.span`
  ${({ theme: { typography } }) => typography.variants.bodyMed};
`;

const PreviewContainer = styled.div`
  position: relative;
  background-color: ${({ theme }) =>
    theme.colors.embroideryTextSelectionPreviewContainerBg};
  height: 560px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${mobile`
    img {
      object-fit: contain;
    }
  `}
`;

const EmbroideryImage = styled.img`
  ${({ showCustomEmbroideryImg }) => {
    if (showCustomEmbroideryImg) {
      return;
    }
    return `
      padding: 75px 20px;
      max-height: 100%;
    `;
  }}
  ${mobile`
    width: 368px;
  `}
`;

const EmbroideryPreview = styled.div`
  width: 95px;
  min-height: 100px;
  position: absolute;
  border: 2px dashed ${({ theme: { colors } }) => colors.white};
  padding: 32px;
  ${({ embroideryConfigs, configKey, location }) => {
    const desktopStyles = getLocationStyles(
      embroideryConfigs,
      configKey,
      location,
      'desktop'
    );
    return `
      top: ${desktopStyles.top};
      right: ${desktopStyles.right};
    `;
  }}
  ${mobile`
    ${({ embroideryConfigs, configKey, location }) => {
      const mobileStyles = getLocationStyles(
        embroideryConfigs,
        configKey,
        location,
        'mobile'
      );
      return `
        top: ${mobileStyles.top};
        right: ${mobileStyles.right};
      `;
    }}
  `}
`;

const DynamicFormattedMessage = FormattedMessage;

const getLocationStyles = (
  embroideryConfigs,
  configKey,
  locationId,
  device
) => {
  const locationConfig = embroideryConfigs.find(
    (config) => config.customizationLocationTemplateId === locationId
  );
  return locationConfig.styles[configKey]?.[device] || {};
};

export default function EmbroideryTextSelection({
  canSkip,
  dialogProps,
  embroideryConfigs,
  handleEmbroideryPreview,
  isPlusSizeSelected,
  isLoading,
  onCTAClick,
  onSkip,
  textCustomizations,
  textOptions,
  locationOptions,
  previewError,
  previewImages,
  previewLoading,
  ...rest
}) {
  const intl = useIntl();

  const { isMobile } = useBreakpoint();

  const { textPersonalizations } = useProductDetailEmbroideryContext();

  const [locationId, setLocationId] = useState(
    textCustomizations[StateFields.TEXT_LOCATION]
  );

  const textPersonalizationsRb = useMemo(
    () =>
      embroideryConfigs.reduce((acc, config) => {
        const { customizationLocationTemplateId, resourceBundle } = config;
        acc[customizationLocationTemplateId] = (
          <DynamicFormattedMessage
            id={resourceBundle.id}
            defaultMessage={resourceBundle.defaultMessage}
          />
        );
        return acc;
      }, {}),
    [embroideryConfigs]
  );

  const embroideryTemplateConfigs = useMemo(
    () =>
      embroideryConfigs.find(
        (config) => config.customizationLocationTemplateId === locationId
      ),
    [embroideryConfigs, locationId]
  );

  const locations = useMemo(() => {
    return locationOptions.map((locationId) => {
      return {
        tab: parseInt(locationId, 10),
        content: textPersonalizationsRb[locationId],
      };
    });
  }, [textPersonalizationsRb, locationOptions]);

  const makePreviewKey = useCallback(
    (item) => {
      if (!item) {
        return null;
      }

      return `${item.line1}::${item.line2}::${item.color.value}::${item.font.value}::${locationId}`;
    },
    [locationId]
  );

  const handleSubmitForm = (values, { setSubmitting }) => {
    onCTAClick({
      [StateFields.TEXT_CUSTOMIZATIONS]: {
        ...values,
        [StateFields.COLOR]: values[StateFields.COLOR].value,
        [StateFields.FONT]: values[StateFields.FONT].value,
        [StateFields.TEXT_LOCATION]: locationId,
      },
    });
    // setSubmitting(false);
  };

  const {
    values: { line1, line2, color, font },
    handleSubmit,
    handleChange,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    initialValues: {
      [StateFields.LINE_1]: textCustomizations[StateFields.LINE_1],
      [StateFields.LINE_2]: textCustomizations[StateFields.LINE_2],
      [StateFields.FONT]: textCustomizations[StateFields.FONT]
        ? {
            name: textCustomizations[StateFields.FONT],
            value: textCustomizations[StateFields.FONT],
          }
        : textOptions[locationId].font[0],
      [StateFields.COLOR]: textCustomizations[StateFields.COLOR]
        ? {
            name: textCustomizations[StateFields.COLOR],
            value: textCustomizations[StateFields.COLOR],
          }
        : textOptions[locationId].color[1],
    },
    onSubmit: handleSubmitForm,
  });

  const currentPreviewKey = makePreviewKey(
    { line1, line2, color, font },
    locationId
  );

  const debouncedEmbroideryPreview = useRef(
    debounce(handleEmbroideryPreview, 500)
  );

  const requestEmbroideryPreviewImage = useCallback(async () => {
    const previewKey = makePreviewKey(
      { line1, line2, color, font },
      locationId
    );

    if (!previewImages[previewKey]) {
      debouncedEmbroideryPreview.current({
        previewKey,
        embroideryOptions: {
          text1: line1,
          text2: line2,
          color: color.value,
          font: font.value,
          masterProductId: textPersonalizations.productId,
          template: textPersonalizations.templateLabel,
        },
      });
    }
  }, [
    line1,
    line2,
    color,
    font,
    textPersonalizations.productId,
    textPersonalizations.templateLabel,
    makePreviewKey,
    locationId,
    previewImages,
  ]);

  useEffect(() => {
    const previewFn = debouncedEmbroideryPreview.current;
    return () => {
      previewFn.cancel();
    };
  }, []);

  useEffect(() => {
    if (line1) {
      requestEmbroideryPreviewImage();
    }
  }, [color, font, requestEmbroideryPreviewImage, line1]);

  const { color: colorOptions, font: fontOptions } = textOptions[locationId];

  const product = useProductContext();
  const embroideryProductImage = getImageUrl({
    product,
    imageType: 'embroidery',
  });

  const showCustomEmbroideryImg = hasEmbroideryImg({ product });

  const embroideryAssetContainers = useAssets(
    ['embroidery_preview_desktop', 'embroidery_preview_mobile'],
    {
      multi: true,
    }
  );

  const { data } = isMobile
    ? embroideryAssetContainers[1]
    : embroideryAssetContainers[0];
  const assets = data?.assets || [];
  const { embroideryConfigKey, embroideryAssetConfig } =
    getEmbroideryConfigKeyAndAsset({
      configs: embroideryTemplateConfigs,
      productCategoryIdList: product?.productCategoryIdList,
    });
  const embroideryAssetImage = assets.find(
    (asset) =>
      asset.label === embroideryAssetConfig?.[isMobile ? 'mobile' : 'desktop']
  )?.imageFilename;

  return (
    <EmbroideryTextSelectionWrapper>
      <PreviewPanel>
        <PreviewContainer>
          <EmbroideryImage
            showCustomEmbroideryImg={showCustomEmbroideryImg}
            src={
              showCustomEmbroideryImg
                ? embroideryProductImage
                : embroideryAssetImage
            }
            alt=""
          />
          <EmbroideryPreview
            configKey={embroideryConfigKey}
            embroideryConfigs={embroideryConfigs}
            location={locationId}
          >
            <EmbroideryPreviewImage
              isLoading={previewLoading}
              previewImageSrc={previewImages[currentPreviewKey] || ''}
              error={previewError}
            />
          </EmbroideryPreview>
        </PreviewContainer>
        {locations?.length > 1 && (
          <SelectorContainer>
            <StyledSelector
              name="location"
              selectionOptions={locations}
              selectedTabId={locationId}
              buttonAutotag="location"
              listAutotag="location-dropdown"
              onOptionSelect={setLocationId}
            />
          </SelectorContainer>
        )}
      </PreviewPanel>

      <CustomizationPanel>
        <TitleContainer>
          <Title>
            <FormattedMessage
              id="site_pdp.embroidery_set_text"
              defaultMessage="Set Up Your Custom Text"
            />
          </Title>
          <Fee>
            <FormattedMessage
              id="site_pdp.embroidery_flat_fee"
              defaultMessage="$5 Flat Fee"
            />
          </Fee>
        </TitleContainer>

        <DropdownWrapper>
          {colorOptions && (
            <SelectWrapper $selectedValue={color.value}>
              <StyledSelect
                id={StateFields.COLOR}
                name={StateFields.COLOR}
                options={colorOptions}
                value={color.value}
                buttonAutotag="thread-color"
                listAutotag="thread-color-dropdown"
                label={intl.formatMessage({
                  id: 'site_pdp.embroidery_thread_color',
                  defaultMessage: 'Thread Color',
                })}
                onChange={(e) =>
                  setFieldValue(StateFields.COLOR, {
                    name: e.target.value,
                    value: e.target.value,
                  })
                }
              >
                <option value={undefined} hidden />
                {colorOptions.map((thread) => (
                  <option
                    value={thread.value}
                    key={thread.value}
                    data-autotag={thread.dataAutotag}
                  >
                    {thread.name}
                  </option>
                ))}
              </StyledSelect>
            </SelectWrapper>
          )}

          {fontOptions && (
            <SelectWrapper $selectedValue={font.value}>
              <StyledSelect
                id={StateFields.FONT}
                name={StateFields.FONT}
                options={fontOptions}
                value={font.value}
                buttonAutotag="font-style"
                listAutotag="font-style-dropdown"
                label={intl.formatMessage({
                  id: 'site_pdp.embroidery_font_style',
                  defaultMessage: 'Font Style',
                })}
                onChange={(e) => {
                  setFieldValue(StateFields.FONT, {
                    name: e.target.value,
                    value: e.target.value,
                  });
                }}
              >
                <option value={undefined} hidden />
                {fontOptions.map((font) => (
                  <option
                    value={font.value}
                    key={font.value}
                    data-autotag={font.dataAutotag}
                  >
                    {font.name}
                  </option>
                ))}
              </StyledSelect>
            </SelectWrapper>
          )}
        </DropdownWrapper>

        <Formik>
          <StyledEmbroideryTextForm onSubmit={handleSubmit}>
            <TextFields>
              <TextFieldWrapper>
                <StyledTextField
                  name={StateFields.LINE_1}
                  required
                  label={
                    <FormattedMessage
                      id="site_pdp.embroidery_line_1"
                      defaultMessage="Line 1 Text"
                    />
                  }
                  maxLength={CHAR_LIMIT}
                  labelPosition="floating"
                  onChange={handleChange}
                  value={line1}
                  data-autotag="line-1-text"
                />
                <CharacterCount>{`${line1.length}/${CHAR_LIMIT}`}</CharacterCount>
              </TextFieldWrapper>
              <TextFieldWrapper>
                <StyledTextField
                  name={StateFields.LINE_2}
                  label={
                    <FormattedMessage
                      id="site_pdp.embroidery_line_2"
                      defaultMessage="Line 2 Text (optional)"
                    />
                  }
                  maxLength={CHAR_LIMIT}
                  labelPosition="floating"
                  onChange={handleChange}
                  value={line2}
                  data-autotag="line-2-text"
                />
                <CharacterCount>{`${line2.length}/${CHAR_LIMIT}`}</CharacterCount>
              </TextFieldWrapper>
            </TextFields>

            <ContinueButton
              variant="primary"
              type="submit"
              isLoading={isSubmitting}
              data-autotag="pdp_embroidery_confirm_btn"
              disabled={!(line1 && font && color)}
            >
              <FormattedMessage
                id="site_pdp.emboridery_continue"
                defaultMessage="Save and Continue"
              />
            </ContinueButton>
          </StyledEmbroideryTextForm>
        </Formik>

        {canSkip && (
          <ContinueButton
            variant="secondary"
            onClick={() => onSkip(PersonalizationTypes.TEXT)}
            data-autotag="pdp_embroidery_confirm_btn"
          >
            <FormattedMessage
              id="site_pdp.emboridery_skip_text"
              defaultMessage="Skip Custom Text"
            />
          </ContinueButton>
        )}
      </CustomizationPanel>
    </EmbroideryTextSelectionWrapper>
  );
}

EmbroideryTextSelection.propTypes = {
  canSkip: PropTypes.bool,
  dialogProps: PropTypes.object,
  embroideryConfigs: PropTypes.array,
  handleEmbroideryPreview: PropTypes.func,
  isLoading: PropTypes.bool,
  isPlusSizeSelected: PropTypes.bool,
  locationOptions: PropTypes.arrayOf(PropTypes.string),
  onCTAClick: PropTypes.func,
  onSkip: PropTypes.func,
  previewError: PropTypes.bool,
  previewImages: PropTypes.object,
  previewLoading: PropTypes.bool,
  textCustomizations: PropTypes.shape({
    [StateFields.LINE_1]: PropTypes.string,
    [StateFields.LINE_2]: PropTypes.string,
    [StateFields.TEXT_LOCATION]: PropTypes.number,
    [StateFields.FONT]: PropTypes.string,
    [StateFields.COLOR]: PropTypes.string,
  }),
  textOptions: PropTypes.shape({
    1: PropTypes.shape({
      color: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      font: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    }),
    2: PropTypes.shape({
      color: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      font: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    }),
  }),
};
